import React from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { Button } from '@progress/kendo-react-buttons';

import { TeamContext } from '../../contexts/team';
import { PlayerContext } from '../../contexts/player';
import { GameDocumentContext } from '../../contexts/game-document';
import { Avatar } from '@progress/kendo-react-layout';
import { GameContext } from '../../contexts/game';
import {
  PlayerChatContext,
  PlayerChatContextProps
} from '../../contexts/player-chat';
import { PlayerState } from '../../types/state';
import { NotificationIndicator } from '../notification-indicator';

interface MobileTabProps {
  activeTab: string;
  selectedPlayer: string;
  otherPlayers: PlayerState[];
  handleSwitch: (chatType: string) => void;
  handleSwitchToDirectChat: () => void;
  chatTypeText: (type: string) => string;
  onChangePlayer: (code: string) => Promise<void>;
}

const ButtonContent = ({
  text,
  newChatCount
}: {
  text: string;
  newChatCount: number;
}) => {
  return (
    <div className={'d-flex align-items-center gap-1'}>
      {text}
      {newChatCount > 0 && (
        <NotificationIndicator counter={newChatCount} bgColor={'#FF5527'} />
      )}
    </div>
  );
};

const MobileTab: React.FC<MobileTabProps> = ({
  activeTab,
  otherPlayers,
  selectedPlayer,
  chatTypeText,
  handleSwitch,
  onChangePlayer,
  handleSwitchToDirectChat
}) => {
  const { newChat } = React.useContext(
    PlayerChatContext
  ) as PlayerChatContextProps;
  const [gameDocumentstate] = React.useContext(GameDocumentContext);
  const [game] = React.useContext(GameContext);
  const [playerState] = React.useContext(PlayerContext);
  const [teamState] = React.useContext(TeamContext);

  const isInLobby = ['waiting', 'ready'].includes(
    playerState.playerState?.status ?? ''
  );
  const preGameSetting = gameDocumentstate.gameDocument?.settings.preGame;
  const inGameSetting =
    game.gameState?.runtime?.inGame ??
    gameDocumentstate.gameDocument?.settings.inGame;

  const showAllPlayerChat = isInLobby
    ? preGameSetting?.showPreGameAllPlayerChat
    : inGameSetting?.showInGameAllPlayerChat;
  const showFacilitatorChat = isInLobby
    ? preGameSetting?.showPreGameFacilitatorChat
    : inGameSetting?.showInGameFacilitatorChat;
  const showTeamChat = isInLobby
    ? preGameSetting?.showPreGameTeamChat
    : inGameSetting?.showInGameTeamChat;
  const showGroupChat = isInLobby
    ? preGameSetting?.showPreGameGroupChat
    : inGameSetting?.showInGameGroupChat;
  const showDirectChat = isInLobby
    ? preGameSetting?.showPreGameDirectChat
    : inGameSetting?.showInGameDirectChat;

  const chatTypeRef = React.useRef<any>(null);
  const [showChatType, setShowChatType] = React.useState(false);
  const playerDirectRef = React.useRef<any>(null);
  const [showPlayerDirect, setShowPlayerDirect] = React.useState(false);

  const toggleChatType = () => setShowChatType((prev) => !prev);
  const togglePlayerDirect = () => setShowPlayerDirect((prev) => !prev);

  const teamCode = playerState.playerState?.teamCode;
  const groupCode =
    teamState.teamState?.groupCode !== '00000000-0000-0000-0000-000000000000'
      ? teamState.teamState?.groupCode
      : undefined;

  const newChatCounter = React.useCallback(
    (
      tabType:
        | 'global-chat'
        | 'group-chat'
        | 'team-chat'
        | 'player-chat'
        | 'facilitator-chat'
    ) => newChat.filter((c) => c.group === tabType).length,
    [newChat.length]
  );

  const newDirectChatCounter = React.useCallback(
    (code: string) =>
      newChat.filter((c) => c.group === 'player-chat' && c.author.id === code)
        .length,
    [newChat.length]
  );

  return (
    <div
      className={`d-flex d-md-none align-items-center justify-content-${activeTab === 'Direct' ? 'between' : 'end'} mb-3`}>
      {activeTab === 'Direct' && (
        <div ref={playerDirectRef}>
          <div
            className="d-flex align-items-center gap-1 cursor-pointer"
            onClick={togglePlayerDirect}>
            {otherPlayers
              .filter((player) => player.code === selectedPlayer)
              .map((player) => (
                <div
                  key={player.code}
                  className={'d-flex gap-2 align-items-center'}
                  style={{
                    wordBreak: 'break-word'
                  }}>
                  <Avatar type={'image'} themeColor={'light'}>
                    <img
                      src={player.avatarImage}
                      className={`h-3 w-3 rounded-circle`}
                      alt={'Player Avatar'}
                    />
                  </Avatar>
                  {player.name}
                </div>
              ))}
            <span className={'material-symbols-outlined'}>
              {showPlayerDirect ? 'expand_less' : 'expand_more'}
            </span>
          </div>

          <Popup
            anchor={playerDirectRef.current}
            show={showPlayerDirect}
            className={'mt-2'}
            anchorAlign={{
              horizontal: 'left',
              vertical: 'bottom'
            }}
            popupAlign={{
              horizontal: 'left',
              vertical: 'top'
            }}>
            <div
              className={
                'd-flex flex-column gap-1 p-2 direct-player-container'
              }>
              {otherPlayers.map((player) => {
                const counter = newDirectChatCounter(player.code ?? '');
                return (
                  <div
                    key={player.code}
                    className={
                      'd-flex align-items-center justify-content-between cursor-pointer'
                    }
                    style={{
                      wordBreak: 'break-word',
                      backgroundColor:
                        selectedPlayer === player.code ? '#003a64' : '',
                      color: selectedPlayer === player.code ? 'white' : 'black',
                      borderRadius: '0.25rem',
                      padding: '6px 12px'
                    }}
                    onClick={() => {
                      onChangePlayer(player.code!);
                      togglePlayerDirect();
                    }}>
                    <div className={'d-flex gap-2 align-items-center '}>
                      <Avatar type={'image'} themeColor={'light'}>
                        <img
                          src={player.avatarImage}
                          className={` rounded-circle`}
                          alt={'Player Avatar'}
                        />
                      </Avatar>
                      {player.name}
                    </div>
                    {counter > 0 && (
                      <NotificationIndicator
                        counter={counter}
                        bgColor={'#FF5527'}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </Popup>
        </div>
      )}

      <div ref={chatTypeRef}>
        <div
          className="d-flex align-items-center gap-1 cursor-pointer"
          onClick={toggleChatType}>
          <h3 className="m-0 fw-bold">{chatTypeText(activeTab)}</h3>
          <span className={'material-symbols-outlined'}>
            {showChatType ? 'expand_less' : 'expand_more'}
          </span>
        </div>

        <Popup
          anchor={chatTypeRef.current}
          show={showChatType}
          className={'mt-2'}
          anchorAlign={{
            horizontal: 'right',
            vertical: 'bottom'
          }}
          popupAlign={{
            horizontal: 'right',
            vertical: 'top'
          }}>
          <div className={'d-flex flex-column gap-1 p-2'}>
            {showAllPlayerChat && (
              <Button
                className={'d-flex justify-content-start'}
                onClick={() => {
                  handleSwitch('All Player');
                  toggleChatType();
                }}
                themeColor={'dark'}
                fillMode={activeTab === 'All Player' ? 'solid' : 'flat'}>
                {ButtonContent({
                  text: chatTypeText('All Player'),
                  newChatCount: newChatCounter('global-chat')
                })}
              </Button>
            )}

            {showGroupChat && groupCode && (
              <Button
                className={'d-flex justify-content-start'}
                onClick={() => {
                  handleSwitch('Group');
                  toggleChatType();
                }}
                themeColor={'dark'}
                fillMode={activeTab === 'Group' ? 'solid' : 'flat'}>
                {ButtonContent({
                  text: chatTypeText('Group'),
                  newChatCount: newChatCounter('group-chat')
                })}
              </Button>
            )}

            {showTeamChat && teamCode && (
              <Button
                className={'d-flex justify-content-start'}
                onClick={() => {
                  handleSwitch('Team');
                  toggleChatType();
                }}
                themeColor={'dark'}
                fillMode={activeTab === 'Team' ? 'solid' : 'flat'}>
                {ButtonContent({
                  text: chatTypeText('Team'),
                  newChatCount: newChatCounter('team-chat')
                })}
              </Button>
            )}

            {showDirectChat && otherPlayers.length > 0 && (
              <Button
                className={'d-flex justify-content-start'}
                onClick={() => {
                  handleSwitchToDirectChat();
                  toggleChatType();
                }}
                themeColor={'dark'}
                fillMode={activeTab === 'Direct' ? 'solid' : 'flat'}>
                {ButtonContent({
                  text: chatTypeText('Direct'),
                  newChatCount: newChatCounter('player-chat')
                })}
              </Button>
            )}

            {showFacilitatorChat && (
              <Button
                className={'d-flex justify-content-start'}
                onClick={() => {
                  handleSwitch('Facilitators');
                  toggleChatType();
                }}
                themeColor={'dark'}
                fillMode={activeTab === 'Facilitators' ? 'solid' : 'flat'}>
                {ButtonContent({
                  text: chatTypeText('Facilitators'),
                  newChatCount: newChatCounter('facilitator-chat')
                })}
              </Button>
            )}
          </div>
        </Popup>
      </div>
    </div>
  );
};

export default MobileTab;
